import React, { useEffect, useRef, useState, useCallback } from 'react';
import { API_URL, apiFetch } from '../config/api';

// Live transport + last-command ACK status for a drone.
// Reads GET /api/drones/:droneId/commands (transport + recent command history).
// Read-only, self-contained: polls on an interval and refetches shortly after a
// command is issued (via refreshSignal).

const TRANSPORT_META = {
  mqtt: { label: 'MQTT', color: '#10b981', title: 'Reliable MQTT link (QoS1, queued across drops)' },
  'edge-ws': { label: 'WS', color: '#3b82f6', title: 'Edge WebSocket (fallback, no delivery guarantee)' },
  http: { label: 'HTTP', color: '#f59e0b', title: 'HTTP polling (dev/lab only)' },
  none: { label: 'OFFLINE', color: '#6b7280', title: 'No command transport available — drone unreachable' },
};

const STATUS_META = {
  acked: { color: '#10b981', label: 'acked' },
  dispatched: { color: '#3b82f6', label: 'sent' },
  failed: { color: '#ef4444', label: 'failed' },
  timeout: { color: '#f59e0b', label: 'timeout' },
  created: { color: '#6b7280', label: 'pending' },
};

function CommandTransportBadge({ droneId, refreshSignal = 0, pollMs = 4000 }) {
  const [transport, setTransport] = useState('none');
  const [last, setLast] = useState(null);
  const [dimmed, setDimmed] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (!droneId) return;
    try {
      const res = await apiFetch(`${API_URL}/drones/${encodeURIComponent(droneId)}/commands?limit=5`);
      if (!res.ok) { setDimmed(true); return; }
      const data = await res.json();
      setDimmed(false);
      setTransport(data.transport || 'none');
      setLast((data.commands && data.commands[0]) || null);
    } catch {
      setDimmed(true);
    }
  }, [droneId]);

  useEffect(() => {
    if (!droneId) return undefined;
    load();
    timerRef.current = setInterval(load, pollMs);
    return () => clearInterval(timerRef.current);
  }, [droneId, pollMs, load]);

  // Refetch shortly after a command is issued so the status updates promptly.
  useEffect(() => {
    if (!refreshSignal) return undefined;
    const t = setTimeout(load, 600);
    return () => clearTimeout(t);
  }, [refreshSignal, load]);

  if (!droneId) return null;

  const tMeta = TRANSPORT_META[transport] || TRANSPORT_META.none;
  const sMeta = last ? (STATUS_META[last.status] || STATUS_META.created) : null;
  const lastTitle = last
    ? `${last.endpoint} via ${last.transport || '—'}${last.result && last.result.message ? ` — ${last.result.message}` : ''}`
    : 'No commands yet';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
      <span
        title={tMeta.title}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
          padding: '2px 8px', borderRadius: '10px', color: '#fff', background: tMeta.color,
          opacity: dimmed ? 0.45 : 1,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
        {tMeta.label}
      </span>

      {sMeta && (
        <span
          title={lastTitle}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '9px', fontWeight: 600, color: 'var(--textSecondary)' }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: sMeta.color }} />
          last: {last.endpoint} · {sMeta.label}
        </span>
      )}
    </div>
  );
}

export default CommandTransportBadge;
